import { useAccount, useMsal } from "@azure/msal-react";
import {
    ActionIcon,
    Box,
    Button,
    Divider,
    Flex,
    Group,
    Modal,
    Text,
    Textarea,
    Tooltip,
} from "@mantine/core";
import {
    IconArrowLeft,
    IconArrowRight,
    IconMessagePlus,
    IconMessages,
    IconThumbDown,
    IconThumbUp,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CTS_USER_CHAT } from "src/db";
import { postAPI } from "src/utils/fetch";
import { AnswerWithSources } from "./AnswerWithSources";
import ConversationThreadList from "./ConversationThreadList";
import { FeedbackType } from "./MessageUtils";
import type { FeedbackSchema } from "./MetadataFeedback/types";
import { sendSMEFeedbackToApi } from "./SMEUtils";
import { AnswerReviewSection } from "./shared/AnswerReviewSection";
import { extractReferencesFromFeedback } from "./shared/referenceUtils";

interface SMEFeedbackModalProps {
    rowData: FeedbackSchema | null;
    onClose: () => void;
    onSuccess: () => void;
    onNext?: () => void;
    onPrevious?: () => void;
    hasNext?: boolean;
    hasPrevious?: boolean;
    flatData?: FeedbackSchema[];
    currentIndex?: number;
}
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
// Predefined comment tags for quick feedback
const POSITIVE_TAGS = [
    {
        label: "Accurate & Complete",
        text: "Response fully addresses the question with accurate information.",
    },
    { label: "Clear & Well-Structured", text: "Easy to understand and well-organized content." },
    { label: "Use of Sources", text: "References are relevant and properly cited." },
    { label: "Professional Tone", text: "Appropriate language and professional style." },
] as const;

const NEGATIVE_TAGS = [
    { label: "Inaccurate Information", text: "Contains factual errors or misconceptions." },
    {
        label: "Incomplete Response",
        text: "Missing key information or provides only partial answer.",
    },
    {
        label: "Poor Source Quality",
        text: "References are irrelevant, unreliable, or improperly cited.",
    },
    { label: "Unclear Language", text: "Confusing, ambiguous, or poorly written content." },
    { label: "Inappropriate Tone", text: "Tone is too casual, too technical, or unprofessional." },
] as const;

const SMEFeedbackModal = ({
    rowData,
    onClose,
    onSuccess,
    onNext,
    onPrevious,
    hasNext,
    hasPrevious,
    flatData,
    currentIndex,
}: SMEFeedbackModalProps) => {
    // Check if feedback already exists
    const existingFeedback: string | undefined = rowData?._source?.evaluations?.sme?.feedback_type;
    const existingComments: string | undefined = rowData?._source?.evaluations?.sme?.comments;
    const { instance } = useMsal();
    // Initialize with existing feedback if available
    const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(() => {
        if (existingFeedback === "positive") return FeedbackType.POSITIVE;
        if (existingFeedback === "negative") return FeedbackType.NEGATIVE;
        return null;
    });
    const [comments, setComments] = useState<string>(existingComments || "");
    const [commentsModalOpen, setCommentsModalOpen] = useState(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [showValidation, setShowValidation] = useState(false);
    const [_hiddenTags, setHiddenTags] = useState<string>(""); // Hidden tag content
    const [showConversationThread, setShowConversationThread] = useState(false);

    useEffect(() => {
        if (rowData) {
            const feedback = rowData._source?.evaluations?.sme?.feedback_type;
            const existingComments = rowData._source?.evaluations?.sme?.comments;

            if (feedback === "positive") {
                setFeedbackType(FeedbackType.POSITIVE);
            } else if (feedback === "negative") {
                setFeedbackType(FeedbackType.NEGATIVE);
            } else {
                setFeedbackType(null);
            }

            setComments(existingComments || "");
            setSelectedTags([]);
            setHiddenTags("");
            setShowValidation(false);
            setShowConversationThread(false);
        } else {
            // Reset when modal closes
            setFeedbackType(null);
            setComments("");
            setSelectedTags([]);
            setHiddenTags("");
            setShowValidation(false);
        }
    }, [rowData]);

    const answer = rowData?._source?.cts_answer?.answer || "";
    const references = extractReferencesFromFeedback(rowData);

    const account = useAccount();
    const apiKey = account?.idToken;

    const { data: conversationThread, isLoading: threadLoading } = useQuery({
        queryKey: ["conversation-thread", rowData?._source?.conversation_id],
        queryFn: async () => {
            if (!rowData?._source?.conversation_id) return null;

            const PATH = `v1/search/${CTS_USER_CHAT}/metadata/docs`;
            const url = new URL(PATH, BACKEND_URL);
            const payload = [{ key: "conversation_id", value: rowData._source.conversation_id }];

            const response = await postAPI({
                url: url.toString(),
                instance: instance,
                payload: payload,
            });

            if (!response) {
                console.warn("No response received for conversation thread query");
                return null;
            }

            const data = await response.json();
            return (
                data?.hits?.hits?.sort((a: unknown, b: unknown) => {
                    const sortA = (a as { sort?: number[] }).sort?.[0] || 0;
                    const sortB = (b as { sort?: number[] }).sort?.[0] || 0;
                    return sortA - sortB;
                }) || []
            );
        },
        enabled: Boolean(rowData?._source?.conversation_id),
        refetchOnWindowFocus: false,
    });

    // Find next unevaluated item for auto-navigation
    const findNextUnevaluated = useCallback(
        (startIndex: number): number => {
            if (!flatData || startIndex >= flatData.length - 1) return -1;

            for (let i = startIndex + 1; i < flatData.length; i++) {
                const item = flatData[i];
                const feedback = item._source?.evaluations?.sme?.feedback_type;
                const hasFeedback =
                    feedback && (feedback === "positive" || feedback === "negative");
                if (!hasFeedback) {
                    return i;
                }
            }
            return -1;
        },
        [flatData],
    );

    // Auto-navigate after successful feedback submission
    const handleSuccessWithAutoNav = useCallback(() => {
        // Check if we should auto-navigate to next unevaluated item
        if (currentIndex !== undefined && flatData && onNext) {
            const nextUnevaluatedIndex = findNextUnevaluated(currentIndex);
            if (nextUnevaluatedIndex !== -1) {
                // Navigate to next item first (updates URL)
                onNext();
                // Then trigger the data refresh
                // The delay prevents modal flicker
                onSuccess();
                return;
            }
        }

        // If no next unevaluated item, refresh data and close
        onSuccess();
        setTimeout(() => {
            onClose();
        }, 100);
    }, [onSuccess, onClose, onNext, currentIndex, flatData, findNextUnevaluated]);

    // Handle feedback button clicks - always open comments modal
    const handleFeedbackClick = useCallback((type: FeedbackType) => {
        setFeedbackType(type);
        setSelectedTags([]); // Clear tags when switching feedback type
        setHiddenTags(""); // Clear hidden tags when switching feedback type
        setCommentsModalOpen(true);
    }, []);

    // Get available tags based on feedback type
    const _availableTags = useMemo(() => {
        return feedbackType === FeedbackType.POSITIVE ? POSITIVE_TAGS : NEGATIVE_TAGS;
    }, [feedbackType]);

    // Get the full comment text (simplified - tags disabled for now)
    const _getFullCommentText = useCallback(() => {
        return comments.trim();
    }, [comments]);

    // Handle tag selection
    const _handleTagClick = useCallback(
        (tag: (typeof POSITIVE_TAGS)[0] | (typeof NEGATIVE_TAGS)[0]) => {
            const isSelected = selectedTags.includes(tag.label);
            const tagPrefix = `tag:${tag.text}`;

            if (isSelected) {
                // Remove tag
                setSelectedTags((prev) => prev.filter((t) => t !== tag.label));
                setHiddenTags((prev) => {
                    // Remove this specific tag from hidden tags
                    const lines = prev.split("\n").filter((line) => line !== tagPrefix);
                    return lines.join("\n");
                });
            } else {
                // Add tag
                setSelectedTags((prev) => [...prev, tag.label]);
                setHiddenTags((prev) => {
                    // Add tag to hidden tags
                    return prev ? `${prev}\n${tagPrefix}` : tagPrefix;
                });
            }
        },
        [selectedTags],
    );

    // Open comments modal for detailed feedback
    const handleCommentsClick = useCallback(() => {
        setCommentsModalOpen(true);
    }, []);

    // Keyboard shortcuts
    useEffect(() => {
        if (!rowData) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            // Don't trigger shortcuts if user is typing in input/textarea
            if (
                event.target instanceof HTMLInputElement ||
                event.target instanceof HTMLTextAreaElement
            ) {
                return;
            }

            switch (event.key) {
                case "ArrowLeft":
                    event.preventDefault();
                    if (hasPrevious && onPrevious) {
                        onPrevious();
                    }
                    break;
                case "ArrowRight":
                    event.preventDefault();
                    if (hasNext && onNext) {
                        onNext();
                    }
                    break;
                case "1":
                    event.preventDefault();
                    handleFeedbackClick(FeedbackType.POSITIVE);
                    break;
                case "2":
                    event.preventDefault();
                    handleFeedbackClick(FeedbackType.NEGATIVE);
                    break;
                case "3":
                    event.preventDefault();
                    handleCommentsClick();
                    break;
                case "Escape":
                    event.preventDefault();
                    if (commentsModalOpen) {
                        setCommentsModalOpen(false);
                    } else {
                        onClose();
                    }
                    break;
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [
        rowData,
        hasPrevious,
        hasNext,
        onPrevious,
        onNext,
        commentsModalOpen,
        onClose,
        handleFeedbackClick,
        handleCommentsClick,
    ]);

    return (
        <>
            <Modal
                opened={rowData !== null}
                onClose={onClose}
                title={
                    <Flex justify="space-between" align="center" w="100%">
                        <Text fw={700} mr="md" fz="xl">
                            SME Evaluation
                        </Text>

                        <Flex align="center" gap="sm">
                            {/* Feedback Buttons */}
                            <Tooltip label="👍 (Key: 1)">
                                <Button
                                    size="sm"
                                    variant={
                                        feedbackType === FeedbackType.POSITIVE
                                            ? "filled"
                                            : "outline"
                                    }
                                    color="green"
                                    onClick={() => handleFeedbackClick(FeedbackType.POSITIVE)}
                                >
                                    <Flex align="center" gap="xs">
                                        <IconThumbUp size={16} />
                                    </Flex>
                                </Button>
                            </Tooltip>

                            <Tooltip label="👎 (Key: 2)">
                                <Button
                                    size="sm"
                                    variant={
                                        feedbackType === FeedbackType.NEGATIVE
                                            ? "filled"
                                            : "outline"
                                    }
                                    color="red"
                                    onClick={() => handleFeedbackClick(FeedbackType.NEGATIVE)}
                                >
                                    <Flex align="center" gap="xs">
                                        <IconThumbDown size={16} />
                                    </Flex>
                                </Button>
                            </Tooltip>
                            {/* Show Thread Button */}
                            {rowData?._source?.conversation_id && (
                                <Tooltip label="Show Full Conversation Thread">
                                    <ActionIcon
                                        variant="light"
                                        size="lg"
                                        onClick={() => setShowConversationThread(true)}
                                        color="blue"
                                        loading={threadLoading}
                                        disabled={
                                            threadLoading ||
                                            !conversationThread ||
                                            conversationThread.length <= 1
                                        }
                                    >
                                        <IconMessages size={18} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                            {/* Comments Button or Text */}
                            {existingComments ? (
                                <Tooltip label="Click to edit comments">
                                    <Text
                                        size="sm"
                                        color="blue"
                                        style={{
                                            cursor: "pointer",
                                            maxWidth: "200px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                        onClick={handleCommentsClick}
                                    >
                                        {existingComments}
                                    </Text>
                                </Tooltip>
                            ) : (
                                <Tooltip label="Add Comments (Key: 3)">
                                    <ActionIcon
                                        variant="light"
                                        size="lg"
                                        onClick={handleCommentsClick}
                                        color="blue"
                                    >
                                        <IconMessagePlus size={18} />
                                    </ActionIcon>
                                </Tooltip>
                            )}

                            {/* Navigation - Fixed width container to prevent pushing */}
                            <Flex w={80} justify="space-between">
                                {hasPrevious && onPrevious ? (
                                    <Tooltip label="Previous Q&A (←)">
                                        <ActionIcon
                                            variant="light"
                                            size="lg"
                                            onClick={onPrevious}
                                            color="gray"
                                        >
                                            <IconArrowLeft size={18} />
                                        </ActionIcon>
                                    </Tooltip>
                                ) : (
                                    <div style={{ width: 36 }} /> // Placeholder to maintain spacing
                                )}

                                {hasNext && onNext ? (
                                    <Tooltip label="Next Q&A (→)">
                                        <ActionIcon
                                            variant="light"
                                            size="lg"
                                            onClick={onNext}
                                            color="blue"
                                        >
                                            <IconArrowRight size={18} />
                                        </ActionIcon>
                                    </Tooltip>
                                ) : (
                                    <div style={{ width: 36 }} /> // Placeholder to maintain spacing
                                )}
                            </Flex>
                        </Flex>
                    </Flex>
                }
                size="90%"
                py="sm"
            >
                <AnswerReviewSection
                    orgCode={rowData?._source?.org_code}
                    origin={rowData?._source?.origin}
                    question={rowData?._source?.question}
                    answer={answer}
                    references={references}
                    questionHeading="h2"
                    metadataVariant="muted"
                    metadata={[
                        {
                            label: "Conversation ID",
                            value: rowData?._source?.conversation_id,
                        },
                        { label: "Response ID", value: rowData?._source?.id },
                    ]}
                    renderAnswer={({ answer: renderedAnswer, references: answerReferences }) => (
                        <AnswerWithSources answer={renderedAnswer} references={answerReferences} />
                    )}
                />
                {/* Always show feedback section at bottom */}

                <Divider my="md" />
                <Text size="sm" mb="xs" color="dimmed">
                    {existingFeedback ? "Update feedback:" : "Provide feedback:"}
                </Text>
                <Flex align="center" gap="sm">
                    <Button
                        size="sm"
                        variant={feedbackType === FeedbackType.POSITIVE ? "filled" : "outline"}
                        color="green"
                        onClick={() => handleFeedbackClick(FeedbackType.POSITIVE)}
                    >
                        <Flex align="center" gap="xs">
                            <IconThumbUp size={16} />
                        </Flex>
                    </Button>
                    <Button
                        size="sm"
                        variant={feedbackType === FeedbackType.NEGATIVE ? "filled" : "outline"}
                        color="red"
                        onClick={() => handleFeedbackClick(FeedbackType.NEGATIVE)}
                    >
                        <Flex align="center" gap="xs">
                            <IconThumbDown size={16} />
                        </Flex>
                    </Button>
                    {!existingComments && (
                        <Tooltip label="Add Comments (Key: 3)">
                            <ActionIcon
                                variant="light"
                                size="lg"
                                onClick={handleCommentsClick}
                                color="blue"
                            >
                                <IconMessagePlus size={18} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Flex>
                {existingComments && (
                    <Box mt="sm" p="sm" style={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                        <Text size="xs" color="dimmed" mb="xs">
                            Current comments:
                        </Text>
                        <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                            {existingComments}
                        </Text>
                    </Box>
                )}
            </Modal>
            <Modal
                opened={commentsModalOpen}
                onClose={() => setCommentsModalOpen(false)}
                title="Add Comments"
                size="md"
                centered
            >
                <div>
                    <Text size="sm" color="dimmed" mb="md">
                        Current evaluation:{" "}
                        <strong>
                            {feedbackType === FeedbackType.POSITIVE
                                ? "👍"
                                : feedbackType === FeedbackType.NEGATIVE
                                  ? "👎"
                                  : existingFeedback
                                    ? existingFeedback === "positive"
                                        ? "👍"
                                        : "👎"
                                    : "None"}
                        </strong>
                    </Text>

                    <Group mb="md">
                        <Button
                            size="sm"
                            variant={feedbackType === FeedbackType.POSITIVE ? "filled" : "outline"}
                            color="green"
                            onClick={() => {
                                setFeedbackType(FeedbackType.POSITIVE);
                                setSelectedTags([]);
                                setHiddenTags("");
                                setComments("");
                            }}
                        >
                            <Flex align="center" gap="xs">
                                <IconThumbUp size={16} />
                            </Flex>
                        </Button>
                        <Button
                            size="sm"
                            variant={feedbackType === FeedbackType.NEGATIVE ? "filled" : "outline"}
                            color="red"
                            onClick={() => {
                                setFeedbackType(FeedbackType.NEGATIVE);
                                setSelectedTags([]);
                                setHiddenTags("");
                                setComments("");
                            }}
                        >
                            <Flex align="center" gap="xs">
                                <IconThumbDown size={16} />
                            </Flex>
                        </Button>
                    </Group>

                    <Textarea
                        data-autofocus
                        value={comments}
                        placeholder="Add your detailed comments here..."
                        rows={6}
                        onChange={(event) => setComments(event.currentTarget.value)}
                        mb="xs"
                        error={
                            showValidation &&
                            feedbackType === FeedbackType.NEGATIVE &&
                            !comments.trim()
                                ? "Please provide comments for negative feedback"
                                : undefined
                        }
                    />

                    <Flex justify="flex-end" gap="md">
                        <Button variant="default" onClick={() => setCommentsModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={async () => {
                                const responseId = rowData?._source.id;
                                if (responseId === undefined || feedbackType === null) return;

                                // Show validation and check for required comments
                                if (feedbackType === FeedbackType.NEGATIVE && !comments.trim()) {
                                    setShowValidation(true);
                                    return;
                                }

                                const success = await sendSMEFeedbackToApi(
                                    apiKey,
                                    responseId,
                                    feedbackType,
                                    comments, // Send user comments directly
                                );
                                if (success) {
                                    setCommentsModalOpen(false);
                                    setShowValidation(false);
                                    handleSuccessWithAutoNav();
                                }
                            }}
                            disabled={
                                feedbackType === null ||
                                (feedbackType === FeedbackType.NEGATIVE && !comments.trim())
                            }
                        >
                            Submit Feedback
                        </Button>
                    </Flex>
                </div>
            </Modal>

            {/* Conversation Thread Modal */}
            <Modal
                opened={showConversationThread}
                onClose={() => setShowConversationThread(false)}
                title="Full Conversation Thread"
                size="90%"
                py="sm"
                centered
            >
                {conversationThread && conversationThread.length > 0 ? (
                    <ConversationThreadList
                        conversationThread={conversationThread}
                        rowData={rowData}
                    />
                ) : (
                    <Text size="sm" color="dimmed" ta="center" py="xl">
                        No conversation thread found
                    </Text>
                )}
            </Modal>
        </>
    );
};

export default SMEFeedbackModal;
